import React from 'react';
import { Link } from 'react-router-dom';
import {
  CLASH_TACTICAL_POINTS_MAX_PER_CLASH,
  TACTICAL_POINTS_PER_ROUND,
  getAugmentDisplayName,
  getAugmentStyleSvgPath,
  getClashAugmentsForHelp,
  getMatchAugmentsForHelp,
  getRoundAugmentsForHelp,
  getStoreStyleName
} from '../utils/augments';
import AboutAugmentDesc from '../components/AboutAugmentDesc';
import TierPyramidDiagram from '../components/TierPyramidDiagram';
import { MATCH_VOTE_ICON_SVG, RARITY_ICON_SVG } from '../components/helpIconTokens';
import { OUTCOME_ICON_SVG, EXACT_ICON_SVG } from '../utils/matchPointsBreakdown';
import {
  BRAZIL_STYLE_ID,
  CRYSTAL_STYLE_ID,
  CYBERPUNK_STYLE_ID,
  FIRE_STYLE_ID,
  GOLD_STYLE_ID,
  SILVER_STYLE_ID
} from 'arena-shared';
import './About.css';

function AboutAugmentCards({ augments }) {
  return (
    <div className="about-augment-cards">
      {augments.map((a) => (
        <div key={a.value} className={`about-augment-card about-augment-card--${a.color}`}>
          <img src={a.svg} alt={a.displayName} className="about-augment-img" />
          <div>
            <h3 className="about-augment-name">{a.displayName}</h3>
            <p className="about-augment-tactical-points">{a.tacticalPointsCost} tactical pts</p>
            <AboutAugmentDesc tagline={a.shortDesc} text={a.desc} />
          </div>
        </div>
      ))}
    </div>
  );
}

/** Flashy play × style samples for the About styles section. */
const ABOUT_STYLE_SHOWCASE = [
  { code: '2x', styleId: GOLD_STYLE_ID },
  { code: 'sa', styleId: CYBERPUNK_STYLE_ID },
  { code: 'bs', styleId: FIRE_STYLE_ID },
  { code: 'gd', styleId: BRAZIL_STYLE_ID },
  { code: 'eif', styleId: SILVER_STYLE_ID },
  { code: 'os', styleId: CRYSTAL_STYLE_ID }
].map((item) => ({
  key: `${item.code}#${item.styleId}`,
  label: getAugmentDisplayName(item.code),
  styleName: getStoreStyleName(item.styleId),
  svg: getAugmentStyleSvgPath(item.code, item.styleId)
}));

const About = () => (
  <div className="about-page">
    <header className="about-header">
      <p className="about-kicker">Football predictions with an edge</p>
      <h1 className="about-title about-title--pop">ScoreClash</h1>
      <p className="about-tagline">
        Predict every scoreline. Swing matches with plays. Climb the table. The closer you are to the
        right score, the more you earn, and the fewer people who nail it, the bigger the payout.
      </p>
      <div className="about-pills" aria-label="Scoring summary">
        <span className="about-pill">
          <img src={EXACT_ICON_SVG} alt="" className="about-pill-icon" />
          Exact · 20
        </span>
        <span className="about-pill">One goal off / near draw · 16</span>
        <span className="about-pill">Close margin · 14</span>
        <span className="about-pill">
          <img src={OUTCOME_ICON_SVG} alt="" className="about-pill-icon" />
          Right outcome · 10
        </span>
        <span className="about-pill">
          <img src={RARITY_ICON_SVG} alt="" className="about-pill-icon" />
          Rarity · up to 2×
        </span>
      </div>
      <Link to="/" className="about-cta sc-btn sc-btn-primary">
        Jump into this week&apos;s slate
      </Link>
      <nav className="about-toc" aria-label="On this page">
        <a href="#how-you-play">How you play it</a>
        <a href="#competitions">Competitions</a>
        <a href="#tiered-leagues">Tiered leagues</a>
        <a href="#match-points">Match points</a>
        <a href="#rarity-multiplier">Rarity multiplier</a>
        <a href="#play-arsenal">Play arsenal</a>
        <a href="#play-styles">Play styles</a>
        <a href="#sponsors">Sponsors</a>
        <a href="#feedback">Feedback</a>
      </nav>
    </header>

    <section id="how-you-play" className="about-section">
      <h2>How you play it</h2>
      <ul className="about-spark">
        <li>
          <strong>Lock your picks</strong> - every scoreline and every play before the first match of
          the round kicks off.
        </li>
        <li>
          <strong>Spend {TACTICAL_POINTS_PER_ROUND} tactical points</strong> - match plays and at most
          one round play share the pool. Most plays once per round; team-side plays (red,
          scored/missed PK) up to twice, either side.
        </li>
        <li>
          <strong>Clash head-to-head</strong> - in tiered and private leagues you are paired each round.
          Spend up to {CLASH_TACTICAL_POINTS_MAX_PER_CLASH} clash tactical points <em>per opponent</em>{' '}
          on Steal, Block, and Ahead, on top of your main tactical budget.
        </li>
      </ul>
    </section>

    <section id="competitions" className="about-section">
      <h2>Competitions</h2>
      <p>
        Lock your picks once; they can count in several competitions. Same scorelines and plays,
        different races.
      </p>
      <ul className="about-spark">
        <li>
          <strong>Round standings</strong> - the public weekly table for each competition. Who crushed
          this round?
        </li>
        <li>
          <strong>Season standings</strong> - the long grind. Points stack across the season on the
          global table.
        </li>
        <li>
          <strong>Tiered leagues</strong> - automatic division ladder for each competition (see below).
          Climb or drop between splits and clash weekly with your group.
        </li>
        <li>
          <strong>Private leagues</strong> - create a league with friends (or join one), invite your
          crew, and clash head-to-head every round.
        </li>
        <li>
          <strong>Coin leagues</strong> - optional side pot on a round: 1 coin in, top three split the
          pot 50% / 30% / 20%.
        </li>
        <li>
          <strong>Sponsored rounds</strong> - put your brand on a round&apos;s standings with a link
          to your product (<a href="#sponsors">see Sponsors</a>).
        </li>
      </ul>
    </section>

    <section id="tiered-leagues" className="about-section">
      <h2>Tiered leagues</h2>
      <p>
        Place picks in a competition and you are automatically put into its division ladder. No invite,
        no signup step: your first locked picks enroll you for that season.
      </p>
      <TierPyramidDiagram />
      <ul className="about-spark">
        <li>
          <strong>Why you are in one</strong> - locking picks for a competition (for example Premier
          League) drops you into an 8-player ladder group for the current season split.
        </li>
        <li>
          <strong>What it is</strong> - a pyramid of small divisions. Higher tiers sit closer to the
          top; each tier fans out into divisions below.
        </li>
        <li>
          <strong>How you climb</strong> - the season is cut into splits. When a split ends, 1st can
          promote up the pyramid and the bottom places can drop down. Your group table only counts
          rounds in that split.
        </li>
        <li>
          <strong>Weekly clashes</strong> - each round you are paired head-to-head inside your group.
          Clash plays swing that ladder&apos;s matchup and standings only.
        </li>
        <li>
          <strong>Separate from the global table</strong> - the same picks still feed public round and
          season standings. The ladder is a parallel race with its own table and clashes.
        </li>
      </ul>
    </section>

    <section id="match-points" className="about-section">
      <h2>Match points</h2>
      <p>
        Closer is better. Correct 1×2 always banks{' '}
        <img src={OUTCOME_ICON_SVG} alt="" className="about-pill-icon" /> <strong>10</strong>, then
        the best precision band stacks on top:
      </p>
      <h3 className="about-subsection-title">Home or away wins</h3>
      <ul className="about-spark">
        <li>
          <img src={EXACT_ICON_SVG} alt="" className="about-pill-icon" />{' '}
          <strong>Exact score</strong>: +10 → <strong>20</strong>
        </li>
        <li>
          <strong>One goal off</strong>: scoreline wrong by exactly one goal total → +6 →{' '}
          <strong>16</strong>
        </li>
        <li>
          <strong>Close correct margin</strong>: same signed goal difference and two goals off total
          (e.g. 2–0 → 3–1) → +4 → <strong>14</strong>
        </li>
        <li>
          <img src={OUTCOME_ICON_SVG} alt="" className="about-pill-icon" />{' '}
          <strong>Outcome only</strong>: otherwise → <strong>10</strong> (including same margin but
          farther, or two goals off with a different margin)
        </li>
      </ul>
      <h3 className="about-subsection-title">Draws</h3>
      <p>
        Margin does not apply on draws (both sides are always level). Closeness is how many goals off
        your scoreline is:
      </p>
      <ul className="about-spark">
        <li>
          <img src={EXACT_ICON_SVG} alt="" className="about-pill-icon" /> <strong>Exact</strong>:
          +10 → <strong>20</strong>
        </li>
        <li>
          <strong>Near</strong>: two goals off total (e.g. 1–1 → 2–2) → +6 → <strong>16</strong>
        </li>
        <li>
          <img src={OUTCOME_ICON_SVG} alt="" className="about-pill-icon" />{' '}
          <strong>Outcome only</strong>: farther draws → <strong>10</strong>
        </li>
      </ul>
    </section>

    <section id="rarity-multiplier" className="about-section">
      <h2 className="about-section-title-with-icon">
        <img src={RARITY_ICON_SVG} alt="" className="about-heading-icon" />
        Rarity multiplier
      </h2>
      <p>
        Outcome is the king! Bold calls pay more. When a match finishes, we check how many players in
        your league called the correct 1×2. The rarer that outcome, the more your{' '}
        <img src={OUTCOME_ICON_SVG} alt="" className="about-pill-icon" /> outcome points are scaled
        (including Double bumps). Precision bonuses stay flat — not scaled. Scored
        PK, missed PK, and red-card bonuses are also flat adds on top and can pay even when your 1×2
        is wrong.
      </p>
      <ul className="about-spark">
        <li>
          <strong>85–100% outcome hit</strong>: 1.0× (no bonus)
        </li>
        <li>
          <strong>75–84%</strong>: 1.1×
        </li>
        <li>
          <strong>65–74%</strong>: 1.2×
        </li>
        <li>
          <strong>55–64%</strong>: 1.3×
        </li>
        <li>
          <strong>45–54%</strong>: 1.4×
        </li>
        <li>
          <strong>35–44%</strong>: 1.5×
        </li>
        <li>
          <strong>25–34%</strong>: 1.6×
        </li>
        <li>
          <strong>15–24%</strong>: 1.7×
        </li>
        <li>
          <strong>10–14%</strong>: 1.8×
        </li>
        <li>
          <strong>5–9%</strong>: 1.9×
        </li>
        <li>
          <strong>0–4%</strong>: 2.0×
        </li>
      </ul>
      <p className="about-fine">
        Based on lock-time community picks in the same league and round. Clash scoring uses your
        post-multiplier match points.
      </p>
      <p>
        Before lock,{' '}
        <img src={MATCH_VOTE_ICON_SVG} alt="" className="about-pill-icon" /> vote for up to three
        matches. The three fixtures with the most votes get an extra <strong>+0.1</strong> on that
        match&apos;s <img src={RARITY_ICON_SVG} alt="" className="about-pill-icon" /> rarity
        multiplier for players who voted for that fixture (for example 1.2× becomes 1.3×).
      </p>
    </section>

    <section id="play-arsenal" className="about-section about-augments">
      <h2>Play arsenal</h2>
      <p className="about-augments-intro">
        This is where you swing matches. Match and round plays share your tactical budget; clash plays
        get a separate cap for weekly head-to-head opponents in tiered and private leagues.
      </p>
      <h3 className="about-subsection-title">Match plays</h3>
      <p className="about-augments-intro">
        Drop on individual fixtures from your picks. Most types once per round; red,
        scored PK, and missed PK up to twice (home or away freely). Double doubles the 10 outcome
        points only; precision bonuses are not doubled.
      </p>
      <AboutAugmentCards augments={getMatchAugmentsForHelp()} />
      <h3 className="about-subsection-title">Round plays</h3>
      <p className="about-augments-intro">
        Pick at most one per round from the same tactical budget. Payouts settle when the round closes.
      </p>
      <AboutAugmentCards augments={getRoundAugmentsForHelp()} />
      <h3 className="about-subsection-title">Clash plays</h3>
      <p className="about-augments-intro">
        Tiered leagues and private leagues. Place on individual matches against your weekly clash
        opponent: each type once per opponent, up to {CLASH_TACTICAL_POINTS_MAX_PER_CLASH} tactical pts
        per opponent (separate from your main tactical budget). Clash plays swing that league&apos;s
        head-to-head and standings (for example Steal moves points within that league only). They do
        not change your main match points, public round tables, or other leagues.
      </p>
      <AboutAugmentCards augments={getClashAugmentsForHelp()} />
    </section>

    <section id="play-styles" className="about-section">
      <h2>Play styles</h2>
      <p>
        Dress up your plays. Collect styles in the store and equip them on match, round, and clash
        plays so your picks look the way you want.
      </p>
      <div className="about-style-showcase" aria-label="Play style samples">
        {ABOUT_STYLE_SHOWCASE.map((item) => (
          <div key={item.key} className="about-style-tile">
            <img src={item.svg} alt="" className="about-style-tile__icon" />
            <span className="about-style-tile__label">{item.label}</span>
            <span className="about-style-tile__style">{item.styleName}</span>
          </div>
        ))}
      </div>
      <Link to="/store" className="about-cta sc-btn sc-btn-primary">
        Browse the store
      </Link>
    </section>

    <section id="sponsors" className="about-section">
      <h2>Sponsors</h2>
      <p>
        Put your brand in front of players when it matters most: on that round&apos;s standings, with
        a link straight to your product or site. Pick a competition and a round, and we will get your
        name on the board.
      </p>
      <div className="about-cta-row">
        <a
          className="about-cta sc-btn sc-btn-primary"
          href="https://forms.gle/1Rg7smXR7tSoXzSX8"
          target="_blank"
          rel="noopener noreferrer"
        >
          Sponsor a round
        </a>
        <a
          className="about-cta sc-btn sc-btn-secondary"
          href="https://x.com/ScoreClash"
          target="_blank"
          rel="noopener noreferrer"
        >
          Message us on X
        </a>
      </div>
    </section>

    <section id="feedback" className="about-section">
      <h2>Feedback</h2>
      <p>
        Got recommendations for the game? Tell us on our{' '}
        <a href="https://x.com/ScoreClash" target="_blank" rel="noopener noreferrer">
          X profile
        </a>
        .
      </p>
      <p className="about-fine">
        <Link to="/privacy">Privacy Policy</Link>
      </p>
    </section>
  </div>
);

export default About;
